import Close from '@mui/icons-material/Close';
import { AppBar, Box, Grid, IconButton, Toolbar } from '@mui/material';
import {
  createPreviewLink,
  type LiveLinksOwnProps,
  type LiveLinksStateProps,
  type SiteLiveLinks
} from 'app/live-links';
import type React from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import type { IframePayload, SelectedRoute } from '../../slice.js';
import { PreviewDrawer } from './content-preview-styles.js';
import PreviewLinkDropdown, { type SelectOptionGroup } from './preview-link-dropdown.js';
import PreviewLinkMenu from './preview-link-menu.js';

export type ContentPreviewStateProps = {
  readonly selection?: SelectedRoute;
};

export type ContentPreviewDispatchProps = {
  readonly closePreview: () => void;
  readonly setPreviewRoute: (selection: SelectedRoute) => void;
  readonly setIframe: (payload: IframePayload) => void;
  readonly showMessage: (type: 'info' | 'error', message: string, timeout: number) => void;
};

export type ContentPreviewProps = ContentPreviewDispatchProps &
  ContentPreviewStateProps &
  LiveLinksOwnProps &
  LiveLinksStateProps;

function serializeSelection({ siteId, routeName }: SelectedRoute): string {
  return `${siteId}|${routeName}`;
}

function parseSelection(str: string): SelectedRoute {
  const parts = str.split('|');
  return { siteId: parts[0], routeName: parts[1] };
}

function getSiteRoutesOptions(sites: SiteLiveLinks[]): SelectOptionGroup[] {
  return sites.map((site) => ({
    heading: site.siteTitle,
    options: site.routes
      .filter((route) => route.preview)
      .map((route) => ({
        label: `${route.path}`,
        value: serializeSelection({
          siteId: site.siteId,
          routeName: route.routeName
        })
      }))
  }));
}

const BorderAppBar = styled(AppBar)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  height: '4rem'
}));

const ContentPreview: React.FC<ContentPreviewProps> = (props) => {
  const { sites, selection, closePreview, setPreviewRoute, projectId, contentId, setIframe, showMessage } = props;
  useEffect(() => closePreview, [closePreview]);

  let preview: React.ReactNode;
  let toolbar: React.ReactNode;
  let previewUrl: string | undefined;

  if (selection) {
    const { routeName, siteId } = selection;

    const site = sites.find((site) => site.siteId === siteId);
    if (site?.token) {
      const route = site.routes.find((route) => route.routeName === routeName);
      if (route) {
        previewUrl = createPreviewLink({
          projectId,
          siteId,
          routeName,
          contentId: route.isPaginated ? contentId : undefined,
          token: site.token
        });
        preview = (
          <iframe
            title="Preview"
            ref={(ref): void => {
              setIframe({ iframe: ref, contentId });
            }}
            src={previewUrl}
            width="100%"
            height="100%"
            sandbox="allow-scripts"
          />
        );
      }
    }

    const handleCopy = (): void => {
      showMessage('info', 'Copied preview link to the clipboard! This link will expire in 24 hours.', 5000);
    };

    toolbar = (
      <Grid container wrap="nowrap" spacing={2}>
        <Grid item>
          <IconButton onClick={closePreview} size="large">
            <Close />
          </IconButton>
        </Grid>
        <Grid item xs zeroMinWidth>
          <PreviewLinkDropdown
            label="Page Preview"
            onChange={(e): void => {
              setPreviewRoute(parseSelection(e.target.value));
            }}
            options={getSiteRoutesOptions(sites)}
            selected={serializeSelection(selection)}
          />
        </Grid>
        <Grid item>{previewUrl && <PreviewLinkMenu url={previewUrl} onCopy={handleCopy} />}</Grid>
      </Grid>
    );
  }

  return (
    <PreviewDrawer open={Boolean(selection)} variant="persistent" anchor="right">
      <Box display="flex" flexDirection="column" width="calc(50vw - 12rem)" height="100%">
        <BorderAppBar position="sticky" color="inherit" elevation={0}>
          <Toolbar>{toolbar}</Toolbar>
        </BorderAppBar>
        {preview}
      </Box>
    </PreviewDrawer>
  );
};

export default ContentPreview;
